import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, TrendingUp } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { MonthRangePicker } from '@/components/MonthRangePicker';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { businessMonthKey, businessToday } from '@/lib/format';

const formatCurrency = (amount: number) => new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);

export default function LoanInstalmentsAnalytics() {
    const [monthRange, setMonthRange] = useState({
        from: startOfMonth(subMonths(businessToday(), 11)),
        to: endOfMonth(businessToday()),
    });

    const fromDate = format(monthRange.from, 'yyyy-MM-dd');
    const toDate = format(monthRange.to, 'yyyy-MM-dd');

    const { data: summaryData, isLoading } = useQuery({
        queryKey: ['loan-instalments-analytics', fromDate, toDate],
        queryFn: async () => {
            const { data: instalments, error } = await supabase
                .from('loan_instalments')
                .select('id, starts_at, amount')
                .gte('starts_at', fromDate)
                .lte('starts_at', toDate + 'T23:59:59');
            if (error) throw error;

            const totalInstalments = instalments?.length || 0;
            const totalAmount = instalments?.reduce((sum, i) => sum + (i.amount || 0), 0) || 0;
            const avgAmount = totalInstalments > 0 ? totalAmount / totalInstalments : 0;

            const monthlyMap = new Map<string, number>();
            const current = new Date(monthRange.from.getFullYear(), monthRange.from.getMonth(), 1);
            while (current <= monthRange.to) {
                const key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
                monthlyMap.set(key, 0);
                current.setMonth(current.getMonth() + 1);
            }

            instalments?.forEach(i => {
                // The stored Ghana month. `starts_at` is a DATE column, so `new Date('2026-08-01')`
                // is UTC midnight and its local getters put every 1st-of-month instalment in the
                // previous bar at any negative offset.
                const key = businessMonthKey(i.starts_at);
                if (key && monthlyMap.has(key)) monthlyMap.set(key, (monthlyMap.get(key) || 0) + (i.amount || 0));
            });

            const monthlyData = Array.from(monthlyMap.entries()).map(([month, amount]) => ({
                month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' }),
                amount,
            }));

            return { totalInstalments, totalAmount, avgAmount, monthlyData };
        },
    });

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
                <Skeleton className="h-80" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-end">
                <MonthRangePicker monthRange={monthRange} onMonthRangeChange={setMonthRange} />
            </div>

            <div className="grid gap-4 md:grid-cols-3">
                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Amount</CardTitle>
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{formatCurrency(summaryData?.totalAmount || 0)}</div>
                        <p className="text-xs text-muted-foreground">{summaryData?.totalInstalments || 0} instalments</p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Avg Instalment</CardTitle>
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{formatCurrency(summaryData?.avgAmount || 0)}</div>
                        <p className="text-xs text-muted-foreground">Per instalment</p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Period</CardTitle>
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{format(monthRange.from, 'MMM yy')} - {format(monthRange.to, 'MMM yy')}</div>
                        <p className="text-xs text-muted-foreground">Selected date range</p>
                    </CardContent>
                </Card>
            </div>

            <Card className="border border-border/40">
                <CardHeader>
                    <CardTitle>Monthly Advance Instalments</CardTitle>
                    <CardDescription>Instalment amounts over time</CardDescription>
                </CardHeader>
                <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                        <AreaChart data={summaryData?.monthlyData}>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                            <XAxis dataKey="month" className="text-xs" />
                            <YAxis className="text-xs" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                            <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }} />
                            <Area type="monotone" dataKey="amount" stroke="hsl(var(--chart-2))" fill="hsl(var(--chart-2))" fillOpacity={0.6} name="Amount" />
                        </AreaChart>
                    </ResponsiveContainer>
                </CardContent>
            </Card>
        </div>
    );
}
